/**
 * Sentence levels: the whole sentence, with the words already typed dimmed
 * and the current one picked out in gold, so the player reads the sentence
 * while typing it word by word (docs/WORDS_AND_SENTENCES.md).
 *
 * Pinned to the screen. Tall stage: just above the word dock, where the eye
 * already is. Wide stage: along the bottom like subtitles, since the word
 * panel floats up near the top of the screen as the climb goes on.
 */
import Phaser from 'phaser';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../../types';
import type { SentenceSpot } from '../../types';

const SIZE = IS_PORTRAIT ? 26 : 28;
const LINE = IS_PORTRAIT ? 36 : 40;
// Wide stage: clear of the word list down the left edge, when that is on.
const MAX_WIDTH = IS_PORTRAIT ? GAME_WIDTH - 64 : 960;
const PAD = 14;
const MAX_WORDS = 20;
/** Above the HUD's bottom edge, below the word panel and the animal. */
const DEPTH = 32;

export class SentenceBar {
  private readonly back: Phaser.GameObjects.Graphics;
  private readonly words: Phaser.GameObjects.Text[] = [];
  /** Where the bar is drawn now, in screen pixels; empty while hidden. */
  private area = new Phaser.Geom.Rectangle(0, 0, 0, 0);
  private faded = false;

  /**
   * @param bottom - The stage y the bar's bottom edge sits at: the top of the
   *   word dock on the tall stage, near the screen's foot on the wide one.
   */
  constructor(
    scene: Phaser.Scene,
    private readonly bottom: number = GAME_HEIGHT - 24,
  ) {
    this.back = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH);
    for (let i = 0; i < MAX_WORDS; i += 1) {
      this.words.push(
        scene.add
          .text(0, 0, '', { fontFamily: FONT_FAMILY, fontSize: `${SIZE}px`, color: PALETTE.snow })
          .setOrigin(0, 0.5)
          .setScrollFactor(0)
          .setDepth(DEPTH + 1)
          .setVisible(false),
      );
    }
  }

  /** Show a sentence with one word picked out, or hide the bar with null. */
  show(spot: SentenceSpot | null): void {
    this.back.clear();
    this.area = new Phaser.Geom.Rectangle(0, 0, 0, 0);
    const shown = spot?.words.slice(0, MAX_WORDS) ?? [];
    this.words.forEach((text, i) => {
      const word = shown[i];
      if (spot === null || word === undefined) {
        text.setVisible(false);
        return;
      }
      const done = i < spot.index;
      const current = i === spot.index;
      text
        .setText(word)
        .setColor(current ? PALETTE.gold : done ? PALETTE.ice : PALETTE.snow)
        .setFontStyle(current ? 'bold' : 'normal')
        .setAlpha(done ? 0.6 : 1)
        .setVisible(true);
    });
    if (spot === null || shown.length === 0) return;
    this.layout(shown.length);
  }

  get bounds(): Phaser.Geom.Rectangle {
    return this.area;
  }

  /**
   * See-through while the animal is behind it, like the keyboard hints: at
   * the start of a wide-stage level the bar sits over the starting floe.
   */
  setFaded(faded: boolean): void {
    if (faded === this.faded) return;
    this.faded = faded;
    const alpha = faded ? 0.25 : 1;
    this.back.setAlpha(alpha);
    for (const word of this.words) word.setAlpha(faded ? 0.25 : 1);
  }

  destroy(): void {
    this.back.destroy();
    for (const word of this.words) word.destroy();
  }

  /** Wrap the words into lines no wider than the stage allows, centred. */
  private layout(count: number): void {
    const space = SIZE * 0.32;
    const widthOf = (i: number): number => this.words[i]?.width ?? 0;
    const lines: number[][] = [];
    let line: number[] = [];
    let width = 0;
    for (let i = 0; i < count; i += 1) {
      if (line.length > 0 && width + space + widthOf(i) > MAX_WIDTH) {
        lines.push(line);
        line = [];
        width = 0;
      }
      width += (line.length > 0 ? space : 0) + widthOf(i);
      line.push(i);
    }
    lines.push(line);

    const lineWidth = (row: number[]): number =>
      row.reduce((sum, i, n) => sum + widthOf(i) + (n > 0 ? space : 0), 0);
    const widest = Math.max(...lines.map(lineWidth));
    const height = lines.length * LINE;
    const top = this.bottom - PAD - height;
    lines.forEach((row, r) => {
      let x = GAME_WIDTH / 2 - lineWidth(row) / 2;
      for (const i of row) {
        this.words[i]?.setPosition(x, top + r * LINE + LINE / 2);
        x += widthOf(i) + space;
      }
    });
    this.area = new Phaser.Geom.Rectangle(
      GAME_WIDTH / 2 - widest / 2 - PAD,
      top - PAD,
      widest + PAD * 2,
      height + PAD * 2,
    );
    this.back.fillStyle(0x0b1220, 0.8);
    this.back.fillRoundedRect(this.area.x, this.area.y, this.area.width, this.area.height, 16);
    this.faded = false;
    this.back.setAlpha(1);
  }
}
